<template>
  <div class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        @click="close" />

      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
        <form @submit.prevent="submitUpdate">
          <!-- Header -->
          <div class="bg-gray-50 px-4 py-3 sm:px-6 flex items-center justify-between">
            <h3 class="text-lg leading-6 font-medium text-gray-900">
              Edit {{ formatReportType(report.reportType) }} Report
            </h3>
            <button
              type="button"
              class="bg-white rounded-md text-gray-400 hover:text-gray-500"
              @click="close">
              <i class="fas fa-times" />
            </button>
          </div>

          <!-- Body -->
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6">
            <!-- Title -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Report Title
              </label>
              <input
                v-model="formData.title"
                type="text"
                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                required>
            </div>

            <!-- Heading -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Report Heading
              </label>
              <input
                v-model="formData.heading"
                type="text"
                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md">
            </div>

            <!-- Intro Content -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Introduction
              </label>
              <textarea
                v-model="formData.intro_content"
                rows="3"
                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Brief introduction or summary for the report" />
            </div>

            <!-- Main Content -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Main Content
              </label>
              <textarea
                v-model="formData.content"
                rows="8"
                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Detailed report content..." />
            </div>

            <!-- Visibility Toggle -->
            <div class="mb-4">
              <label class="flex items-center">
                <input
                  v-model="formData.is_public"
                  type="checkbox"
                  class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded">
                <span class="ml-2 text-sm text-gray-700">
                  Make report publicly accessible
                </span>
              </label>
            </div>

            <!-- Citations Section -->
            <div class="mb-4">
              <div class="flex items-center justify-between mb-3">
                <label class="block text-sm font-medium text-gray-700">
                  Citations & References
                </label>
                <button
                  type="button"
                  class="text-sm text-indigo-600 hover:text-indigo-500"
                  @click="addCitation">
                  <i class="fas fa-plus mr-1" />
                  Add Citation
                </button>
              </div>

              <div
                v-if="citations.length > 0"
                class="space-y-3">
                <div
                  v-for="(citation, index) in citations"
                  :key="index"
                  class="border border-gray-200 rounded-lg p-3">
                  <div class="flex justify-between items-start mb-2">
                    <span class="text-sm font-medium text-gray-700">Citation #{{ index + 1 }}</span>
                    <button
                      type="button"
                      class="text-red-500 hover:text-red-700"
                      @click="removeCitation(index)">
                      <i class="fas fa-trash" />
                    </button>
                  </div>

                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <input
                        v-model="citation.source_name"
                        type="text"
                        placeholder="Source Name"
                        class="w-full text-sm border-gray-300 rounded-md"
                        required>
                    </div>
                    <div>
                      <input
                        v-model="citation.url"
                        type="url"
                        placeholder="URL"
                        class="w-full text-sm border-gray-300 rounded-md"
                        required>
                    </div>
                    <div>
                      <input
                        v-model="citation.title"
                        type="text"
                        placeholder="Title (optional)"
                        class="w-full text-sm border-gray-300 rounded-md">
                    </div>
                    <div>
                      <input
                        v-model="citation.description"
                        type="text"
                        placeholder="Description (optional)"
                        class="w-full text-sm border-gray-300 rounded-md">
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-else
                class="text-center py-4 text-gray-400 text-sm">
                No citations added yet
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="submit"
              :disabled="isSubmitting"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2
                     bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700
                     focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                     sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed">
              <span v-if="isSubmitting">
                <i class="fas fa-spinner fa-spin mr-2" />
                Updating...
              </span>
              <span v-else>Update Report</span>
            </button>
            <button
              type="button"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2
                     bg-white text-base font-medium text-gray-700 hover:bg-gray-50
                     focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                     sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              @click="close">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import mdtoast from '@dmuy/toast'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  },
  reportId: {
    type: String,
    required: true
  },
  report: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close', 'reportUpdated'])

// State
const formData = ref({
  title: '',
  heading: '',
  intro_content: '',
  content: '',
  is_public: true
})

const citations = ref([])
const isSubmitting = ref(false)

// Methods
const loadReport = async() => {
  try {
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/monthly_reports/${props.reportId}`)

    const report = response.data.report
    formData.value = {
      title: report.title || '',
      heading: report.heading || '',
      intro_content: report.introContent || '',
      content: report.content || '',
      is_public: report.isPublic !== false
    }

    // Load citations
    if (response.data.citations) {
      citations.value = response.data.citations
    }
  } catch (err) {
    mdtoast.error('Failed to load report details', { duration: 3000 })
    console.error('Error loading report:', err)
  }
}

const addCitation = () => {
  citations.value.push({
    source_name: '',
    url: '',
    title: '',
    description: '',
    position: citations.value.length
  })
}

const removeCitation = (index) => {
  citations.value.splice(index, 1)
  // Update positions
  citations.value.forEach((c, i) => {
    c.position = i
  })
}

const submitUpdate = async() => {
  if (!formData.value.title) {
    mdtoast.error('Please enter a report title', { duration: 3000 })
    return
  }

  isSubmitting.value = true

  try {
    const response = await axios.patch(
      `/ajax/organizations/${props.organizationId}/monthly_reports/${props.reportId}`,
      {
        monthly_report: formData.value,
        citations: citations.value.filter((c) => c.source_name && c.url)
      }
    )

    emit('reportUpdated', response.data.report)
    mdtoast.success('Report updated successfully', { duration: 3000 })
    close()
  } catch (err) {
    mdtoast.error('Failed to update report. Please try again.', { duration: 3000 })
    console.error('Error updating report:', err)
  } finally {
    isSubmitting.value = false
  }
}

const close = () => {
  emit('close')
}

const formatReportType = (type) => {
  const typeMap = {
    month_1: 'Month 1',
    month_2: 'Month 2',
    month_3_plus: 'Month 3+'
  }
  return typeMap[type] || type
}

// Lifecycle
onMounted(() => {
  loadReport()
})
</script>
